import React from 'react';

import { CircleQuestion, Plus } from '@gravity-ui/icons';
import { Button, Icon, Label, Select } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { installableAppLabels } from 'client/blocks/Settings/channels/labels';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { ApkInfoWithTargets } from 'client/types';
import * as keyset from 'i18n/AppForm';
import * as settingsLabelsKeyset from 'i18n/SettingsLabels';

import styles from './ApkTargetPlatform.module.scss';

const t = i18n(keyset);
const tSettings = i18n(settingsLabelsKeyset);

interface Props {
    availableTargetPlatforms?: string[];
    selectedTargetPlatforms?: string[];
    disabled: boolean;
    updateAppVersion: (updatedValues: Partial<ApkInfoWithTargets>) => void;
    error: string | undefined;
}

export const ApkTargetPlatform: React.FC<Props> = ({
    availableTargetPlatforms = [],
    selectedTargetPlatforms = [],
    disabled,
    updateAppVersion,
    error,
}) => {
    const handleAddSelectedPlatforms = (addedPlatforms: string[]) => {
        if (disabled) return;
        const updatedPlatforms = Array.from(new Set([...selectedTargetPlatforms, ...addedPlatforms]));
        updateAppVersion({ targetPlatforms: updatedPlatforms });
    };

    const handleDeleteSelectedPlatform = (deleteCandidate: string) => {
        if (disabled) return;
        updateAppVersion({
            targetPlatforms: [...selectedTargetPlatforms].filter((platform) => platform !== deleteCandidate),
        });
    };

    return (
        <Field
            fieldTitle={installableAppLabels.targetPlatforms}
            fieldDescription={t('target-platforms-description')}
            error={error}
            required
            size="m"
        >
            <div className={styles.selectedTargetPlatform__container}>
                {!!selectedTargetPlatforms?.length &&
                    [...selectedTargetPlatforms].sort().map((selectedPlatform) => (
                        <Label
                            key={selectedPlatform}
                            className={styles.selectedTargetPlatform__label}
                            onCloseClick={() => handleDeleteSelectedPlatform(selectedPlatform)}
                            size="s"
                            theme="info"
                            type={disabled ? 'default' : 'close'}
                        >
                            {selectedPlatform}
                        </Label>
                    ))}
            </div>
            <Select
                className={styles.targetPlatformsSelect}
                renderControl={({ onClick, onKeyDown, ref }) => {
                    return (
                        <Button ref={ref} onClick={onClick} extraProps={{ onKeyDown }} view="outlined" size="s">
                            {disabled ? tSettings('target-platforms') : t('add-target-platforms')}
                            <Icon data={disabled ? CircleQuestion : Plus} />
                        </Button>
                    );
                }}
                onUpdate={handleAddSelectedPlatforms}
                multiple
                width="max"
                size="l"
                filterable
                disabled={disabled}
            >
                {availableTargetPlatforms.map((platform) => (
                    <Select.Option key={platform} value={platform}>
                        {platform}
                    </Select.Option>
                ))}
            </Select>
        </Field>
    );
};
